import { useContext, useEffect, useRef } from 'react';

import { FocusAttributeContext } from '@/dialogs/item-editor/Context/FocusAttributeContext';

type RefWithFocusAndClear = {
  focus: () => void;
  clear?: () => void;
};

const useFocusOnAttribute = <T extends RefWithFocusAndClear>(
    attribute: { name: string },
    setState?: (value: boolean) => void,
    options?: { clearOnFocus?: boolean }
): React.RefObject<T> => {
    const FocusContext = useContext(FocusAttributeContext);

    if (!FocusContext) {
        throw new Error('FocusContext must be used within a FocusAttributeProvider');
    }

    const { focusAttribute, setSectionOpen, sectionOpen } = FocusContext;
    const focusRef = useRef<T>(null);

    useEffect(() => {
        const shouldFocus = attribute.name === focusAttribute?.name;

        if (shouldFocus) {
            const element = focusRef.current as HTMLElement | null;
            if (element) {
                // Focus the element
                focusRef.current?.focus?.();

                // Optionally clear it
                if (options?.clearOnFocus && typeof focusRef.current?.clear === 'function') {
                    focusRef.current.clear();
                }
                setSectionOpen(false);
                return;
            }

            // Fallback for plain input elements (non-components)
            if (!focusRef.current && setState) {
                setState(true);
            }

            setSectionOpen(false);
        }
    }, [focusAttribute, sectionOpen, setSectionOpen, attribute.name, setState, options]);

    return focusRef;
};

export default useFocusOnAttribute;
